import { Form, Input, Steps } from "antd"
import { AnimatePresence } from "motion/react"
import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import styled from "styled-components"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import CheckboxWrapper from "#blocks/Form/CheckboxWrapper"
import {
  type ThemeContextType,
  useThemeContext,
} from "#contexts/ThemeContextProvider"
import {
  ScheduledEnrollmentEmail,
  TryoutProgressFormData,
} from "#customTypes/composedTypes/tryoutTypes"
import { setNestedFormField } from "#utils/setNestedFormField"
import { getCurrentStep } from "#utils/tryoutHelpers"

import TryoutProgressStep0 from "./TryoutProgressStep0"
import TryoutProgressStep1 from "./TryoutProgressStep1"
import TryoutProgressStep2 from "./TryoutProgressStep2"
import TryoutProgressStep3 from "./TryoutProgressStep3"
import useTryoutSessionOptions from "./useTryoutSessionOptions"

const SSelectedStep = styled(Flex)<{
  isSelected: boolean
  theme: ThemeContextType
}>`
  background-color: ${({ isSelected, theme }) =>
    isSelected ? theme.highlight : "transparent"};
  border: 1px solid
    ${({ isSelected, theme }) => (isSelected ? theme.highlight : "transparent")};
  padding: 10px;
  border-radius: 5px;
  transition:
    background-color 0.3s ease-in-out,
    border-color 0.3s ease-in-out;
  width: 210px;
`

export type StepSetter = (step: 0 | 1 | 2 | 3) => void

const TryoutProgressForm = ({
  initialValues,
  isReady,
  stepRef,
  onCancelScheduledEmail,
}: {
  initialValues: TryoutProgressFormData
  isReady: boolean
  stepRef?: MutableRefObject<StepSetter | undefined>
  onCancelScheduledEmail?: (
    scheduled: ScheduledEnrollmentEmail
  ) => Promise<{ canceledCount: number; alreadyGoneCount: number }>
}) => {
  const isLoadedRef = useRef(false)
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0)
  const theme = useThemeContext()
  const form = Form.useFormInstance<TryoutProgressFormData>()

  // Watch Step 2 values for computing derived sidebar fields
  const didPassTryout = Form.useWatch(["step2", "didPassTryout"], form) as
    | boolean
    | undefined
  const failureReason = Form.useWatch(["step2", "failureReason"], form) as
    | string
    | undefined
  const isLevelUpdated = Form.useWatch(["step2", "isLevelUpdated"], form) as
    | boolean
    | undefined

  // Watch skill levels to auto-check "Level updated"
  const coreSkillLevel = Form.useWatch(["step2", "coreSkillLevel"], form)
  const digitalSkillLevel = Form.useWatch(["step2", "digitalSkillLevel"], form)
  const sculptureSkillLevel = Form.useWatch(
    ["step2", "sculptureSkillLevel"],
    form
  )

  const tryoutSessionOptions = useTryoutSessionOptions(initialValues?.sessionId)

  useEffect(() => {
    if (isReady && !isLoadedRef.current) {
      const stepFromData = getCurrentStep(initialValues)
      setStep(stepFromData)
      isLoadedRef.current = true
    }
  }, [isReady, initialValues])

  // Auto-check "Level updated" when any skill level has a value
  const hasSkillLevel = !!(
    coreSkillLevel ||
    digitalSkillLevel ||
    sculptureSkillLevel
  )
  useEffect(() => {
    if (hasSkillLevel && didPassTryout === true) {
      setNestedFormField(form, ["step2", "isLevelUpdated"], true)
    }
  }, [hasSkillLevel, didPassTryout, form])

  // Computed values for Step 2 sidebar display
  // Use strict boolean checks — hidden Input can coerce undefined → ""
  const isResultDecided = didPassTryout === true || didPassTryout === false
  const isResultDocumented =
    didPassTryout === true
      ? hasSkillLevel || !!isLevelUpdated
      : didPassTryout === false
        ? !!failureReason?.trim()
        : false

  // Sync computed values to form fields for CheckboxWrapper binding
  useEffect(() => {
    setNestedFormField(form, ["step2", "isResultDecided"], isResultDecided)
  }, [isResultDecided, form])

  useEffect(() => {
    setNestedFormField(
      form,
      ["step2", "isResultDocumented"],
      isResultDocumented
    )
  }, [isResultDocumented, form])

  // Expose setStep to parent via ref for cross-step validation navigation
  useEffect(() => {
    if (stepRef) {
      stepRef.current = setStep
    }
  }, [stepRef, setStep])

  const handleStepChange = useCallback((value: number) => {
    const newStep = value as 0 | 1 | 2 | 3
    setStep(newStep)
  }, [])

  if (!isReady) return <div>Loading...</div>

  return (
    <Flex gap={30} style={{ marginTop: 30 }} grow>
      <Flex style={{ maxHeight: 800, marginTop: 20, width: 235 }}>
        <Steps
          onChange={handleStepChange}
          direction="vertical"
          current={step}
          style={{ height: 700, marginBottom: 100 }}
          items={[
            // Step 0: Scheduled
            {
              title: "Scheduled",
              description: (
                <SSelectedStep col isSelected={step === 0} theme={theme}>
                  <CheckboxWrapper
                    size="small"
                    name={["step0", "hasSelectedSession"]}
                    label="Session selected"
                  />
                  <CheckboxWrapper
                    size="small"
                    name={["step0", "isTryoutFeePaid"]}
                    label="Fee paid"
                  />
                </SSelectedStep>
              ),
            },
            // Step 1: Arrived
            {
              title: "Arrived",
              description: (
                <SSelectedStep col isSelected={step === 1} theme={theme}>
                  <CheckboxWrapper
                    size="small"
                    name={["step1", "isTourCompleted"]}
                    label="Studio tour"
                  />
                  <CheckboxWrapper
                    size="small"
                    name={["step1", "teacherHasEvalForm"]}
                    label="Teacher has eval form"
                  />
                  <CheckboxWrapper
                    size="small"
                    name={["step1", "hasEmergencyContact"]}
                    label="Emergency contact"
                  />
                  <CheckboxWrapper
                    size="small"
                    name={["step1", "hasSignedReleaseForm"]}
                    label="Release form signed"
                  />
                </SSelectedStep>
              ),
            },
            // Step 2: In Class
            {
              title: "In Class",
              description: (
                <SSelectedStep col isSelected={step === 2} theme={theme}>
                  <CheckboxWrapper
                    size="small"
                    name={["step2", "isEvalScanned"]}
                    label="Eval scanned"
                  />
                  {/* Outcome checkbox + result text below */}
                  <Flex col gap={2}>
                    <Flex gap={8} style={{ alignItems: "center" }}>
                      <CheckboxWrapper
                        name={["step2", "isResultDecided"]}
                        size="small"
                        value={isResultDecided}
                        label={
                          didPassTryout === true
                            ? "Outcome: ✅ Passed"
                            : didPassTryout === false
                              ? "Outcome: ❌ Failed"
                              : "Outcome"
                        }
                      />
                    </Flex>
                  </Flex>
                  {/* Documentation status */}
                  {isResultDecided && (
                    <CheckboxWrapper
                      size="small"
                      name={["step2", "isResultDocumented"]}
                      label={
                        isResultDocumented
                          ? didPassTryout === true
                            ? "Level updated"
                            : "Reason provided"
                          : "Result not decided"
                      }
                    />
                  )}
                </SSelectedStep>
              ),
            },
            // Step 3: Pickup
            {
              title: "Pickup",
              description: (
                <SSelectedStep col isSelected={step === 3} theme={theme}>
                  <CheckboxWrapper
                    size="small"
                    name={["step3", "hasEvalFeedback"]}
                    label="Eval feedback"
                  />
                  <CheckboxWrapper
                    size="small"
                    name={["step3", "hasGivenSwag"]}
                    label="Swag given"
                  />
                  <CheckboxWrapper
                    size="small"
                    name={["step3", "hasSelectedSessions"]}
                    label="Sessions selected"
                  />
                  <CheckboxWrapper
                    size="small"
                    name={["step3", "enrollmentReceiptSent"]}
                    label="Receipt sent"
                  />
                </SSelectedStep>
              ),
            },
          ]}
        />
      </Flex>

      <Flex
        style={{
          borderLeft: "1px solid #f0f0f0",
          overflowY: "auto",
          width: "100%",
          marginRight: 40,
        }}
      >
        <AnimatePresence mode="popLayout">
          <AnimateInFlex
            animation={ANIMATION_BY_KEY.leftSm}
            animationKey={step.toString()}
            grow
          >
            {step === 0 && (
              <TryoutProgressStep0
                tryoutSessionOptions={tryoutSessionOptions}
              />
            )}
            {step === 1 && <TryoutProgressStep1 />}
            {step === 2 && <TryoutProgressStep2 />}
            {step === 3 && (
              <TryoutProgressStep3
                onCancelScheduledEmail={onCancelScheduledEmail}
              />
            )}
          </AnimateInFlex>
        </AnimatePresence>
      </Flex>

      {/* Hidden form fields */}
      <Form.Item name="id" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="semester" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="tryoutFilterDate" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="studentId" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="sessionId" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      {/* Step 2 fields - persisted here so they survive step changes */}
      <Form.Item name={["step2", "didPassTryout"]} style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name={["step2", "failureReason"]} style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name={["step2", "isLevelUpdated"]} style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
      {/* Computed fields for sidebar display */}
      <Form.Item
        name={["step2", "isResultDecided"]}
        style={{ display: "none" }}
      >
        <Input type="hidden" />
      </Form.Item>
      <Form.Item
        name={["step2", "isResultDocumented"]}
        style={{ display: "none" }}
      >
        <Input type="hidden" />
      </Form.Item>
      {/* Step 3 receipt tracking */}
      <Form.Item
        name={["step3", "enrollmentReceiptSent"]}
        style={{ display: "none" }}
      >
        <Input type="hidden" />
      </Form.Item>
      <Form.Item
        name={["step3", "scheduledEnrollmentEmail"]}
        style={{ display: "none" }}
      >
        <Input type="hidden" />
      </Form.Item>
      {/* Cross-step validator: skill level required when student passed */}
      <Form.Item
        name="_skillLevelRequired"
        style={{ display: "none" }}
        dependencies={[
          ["step2", "didPassTryout"],
          ["step2", "coreSkillLevel"],
          ["step2", "digitalSkillLevel"],
          ["step2", "sculptureSkillLevel"],
        ]}
        rules={[
          ({ getFieldValue }) => ({
            validator() {
              const didPass = getFieldValue(["step2", "didPassTryout"])
              if (didPass !== true) return Promise.resolve()
              const core = getFieldValue(["step2", "coreSkillLevel"])
              const digital = getFieldValue(["step2", "digitalSkillLevel"])
              const sculpture = getFieldValue(["step2", "sculptureSkillLevel"])
              if (core || digital || sculpture) return Promise.resolve()
              return Promise.reject(
                new Error("Please select a student skill level")
              )
            },
          }),
        ]}
      >
        <Input type="hidden" />
      </Form.Item>
    </Flex>
  )
}

export default TryoutProgressForm
